import React, {useCallback, useEffect, useMemo, useState} from "react";
import PropTypes from "prop-types";
import {useTranslation} from "react-i18next";
import toast from "react-hot-toast";
import {DateTime} from "luxon";

import BoardPopup from "../../../components/board/board-popup";
import ButtonSubmit from "../../../components/common/form/button-submit";
import InputQuantityNumber from "../../../components/common/form/input-quantity-number";
import InputDate from "../../../components/common/form/input-date";
import LoaderWrapper from "../../../components/common/loader/loader-wrapper";
import {TrashIcon} from "@heroicons/react/outline";
import UserInitial from "../../../components/common/table/user-initial";
import NumberFormat from "react-number-format";
import {getFormattedDate} from "../../../utils/timeUtils";
import {classNames} from "../../../utils/Utils";

import {
    createDocumentBodyEvent,
    deleteDocumentBodyEvent,
    fetchDocumentBodyEvents,
    updateDocumentBodyEvent,
    updateDocumentBodyRow,
} from "../services/document-service";

const EVENT_TYPE_CONFIRMED_QTY = "CONFIRMED_QTY";
const EVENT_TYPE_REQUEST_CONFIRMED_QTY = "REQUEST_CONFIRMED_QUANTITY";

const getCurrentServerDateTime = () => (
    DateTime.local().toFormat("yyyy-MM-dd HH:mm:ss")
);

const normalizeEventDate = (value) => {
    if (!value) {
        return "";
    }

    let parsed = DateTime.fromSQL(value);
    if (!parsed.isValid) {
        parsed = DateTime.fromISO(value);
    }
    if (!parsed.isValid) {
        parsed = DateTime.fromFormat(value, "yyyy-MM-dd HH:mm:ss");
    }
    if (!parsed.isValid) {
        return "";
    }
    return parsed.toFormat("yyyy-MM-dd HH:mm:ss");
};

const normalizeEventsPayload = (payload) => {
    if (!payload) {
        return {events: [], total: 0};
    }

    if (Array.isArray(payload)) {
        return {events: payload, total: payload.length};
    }

    if (Array.isArray(payload?.events)) {
        const totalRows = typeof payload.total_rows === "number"
            ? payload.total_rows
            : payload.events.length;
        return {events: payload.events, total: totalRows};
    }

    if (Array.isArray(payload?.data)) {
        return {events: payload.data, total: payload.data.length};
    }

    return {events: [], total: 0};
};

const toNumericOrNull = (value) => {
    if (value === null || value === undefined || value === "") {
        return null;
    }
    const numeric = Number(value);
    return Number.isFinite(numeric) ? numeric : null;
};

const resolveEventQuantityValue = (eventItem) => {
    if (!eventItem || typeof eventItem !== "object") {
        return 0;
    }
    const candidate = toNumericOrNull(
        eventItem.quantity
        ?? eventItem.confirmed_quantity
        ?? eventItem.qty
        ?? eventItem.value
    );
    return candidate ?? 0;
};

const sumEventQuantities = (entries) => {
    if (!Array.isArray(entries) || entries.length === 0) {
        return 0;
    }
    return entries.reduce((total, item) => total + resolveEventQuantityValue(item), 0);
};

const isRequestProcessed = (eventItem) => {
    if (!eventItem || typeof eventItem !== "object") {
        return false;
    }
    const statusValue = typeof eventItem.status === "string" ? eventItem.status.toLowerCase() : "";
    return statusValue === "confirmed" || statusValue === "rejected";
};

const filterPendingRequestEvents = (entries) => {
    if (!Array.isArray(entries)) {
        return [];
    }
    return entries.filter((item) => !isRequestProcessed(item));
};

const formatQuantityDisplay = (value) => {
    if (value === null || value === undefined) {
        return "0";
    }
    try {
        return new Intl.NumberFormat(undefined, {maximumFractionDigits: 4}).format(value);
    } catch (error) {
        return String(value);
    }
};

const DocumentBodyConfirmedQuantityModal = ({
    isOpen,
    onClose,
    companyId,
    documentId,
    bodyId,
    onEventsChange,
    maxQuantity,
    initialQuantityTotal,
}) => {
    const {t} = useTranslation();
    const [isFetching, setIsFetching] = useState(false);
    const [events, setEvents] = useState([]);
    const [totalEvents, setTotalEvents] = useState(0);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [rowLoading, setRowLoading] = useState({});
    const [hasLoadedEvents, setHasLoadedEvents] = useState(false);
    const [activeTab, setActiveTab] = useState("confirmed");
    const [newEntry, setNewEntry] = useState(() => ({
        quantity: "",
        eventDate: getCurrentServerDateTime(),
    }));
    const [requestedEvents, setRequestedEvents] = useState([]);
    const [requestedTotal, setRequestedTotal] = useState(0);

    const resolvedMaxQuantity = useMemo(() => {
        const numeric = toNumericOrNull(maxQuantity);
        return numeric === null ? null : numeric;
    }, [maxQuantity]);
    const [managedMaxQuantity, setManagedMaxQuantity] = useState(() => resolvedMaxQuantity);

    useEffect(() => {
        setManagedMaxQuantity(resolvedMaxQuantity);
    }, [resolvedMaxQuantity]);

    const effectiveMaxQuantity = useMemo(() => (
        managedMaxQuantity ?? resolvedMaxQuantity ?? null
    ), [managedMaxQuantity, resolvedMaxQuantity]);

    const initialConfirmedQuantity = useMemo(() => {
        const numeric = toNumericOrNull(initialQuantityTotal);
        return numeric ?? 0;
    }, [initialQuantityTotal]);

    const currentEventsQuantity = useMemo(() => sumEventQuantities(events), [events]);

    const effectiveEventsQuantity = useMemo(() => (
        hasLoadedEvents
            ? currentEventsQuantity
            : Math.max(currentEventsQuantity, initialConfirmedQuantity)
    ), [currentEventsQuantity, hasLoadedEvents, initialConfirmedQuantity]);

    const remainingQuantity = useMemo(() => {
        if (effectiveMaxQuantity === null) {
            return null;
        }
        const remaining = effectiveMaxQuantity - effectiveEventsQuantity;
        return remaining > 0 ? remaining : 0;
    }, [effectiveEventsQuantity, effectiveMaxQuantity]);

    const isAtCapacity = useMemo(() => (
        effectiveMaxQuantity !== null
        && remainingQuantity !== null
        && remainingQuantity <= 0
    ), [effectiveMaxQuantity, remainingQuantity]);

    const formattedRemainingQuantity = useMemo(() => (
        remainingQuantity === null ? null : formatQuantityDisplay(remainingQuantity)
    ), [remainingQuantity]);

    const formattedMaxQuantity = useMemo(() => (
        effectiveMaxQuantity === null ? null : formatQuantityDisplay(effectiveMaxQuantity)
    ), [effectiveMaxQuantity]);

    const hasConfirmedEvents = events.length > 0 || totalEvents > 0;
    const hasRequestedEvents = requestedEvents.length > 0 || requestedTotal > 0;

    const notifyParent = useCallback((payload) => {
        if (onEventsChange) {
            onEventsChange(payload ?? null);
        }
    }, [onEventsChange]);

    const applyEventsPayload = useCallback((payload) => {
        const {events: list, total} = normalizeEventsPayload(payload);
        setEvents(list);
        setTotalEvents(total);
        setHasLoadedEvents(true);
        return {events: list, total};
    }, []);

    const applyRequestedEventsPayload = useCallback((payload) => {
        const {events: list} = normalizeEventsPayload(payload);
        const pendingList = filterPendingRequestEvents(list);
        setRequestedEvents(pendingList);
        setRequestedTotal(pendingList.length);
        return {events: pendingList, total: pendingList.length};
    }, []);

    const handleApiListResponse = useCallback((payload) => {
        const result = applyEventsPayload(payload);
        notifyParent(result);
        return result;
    }, [applyEventsPayload, notifyParent]);

    const fetchEvents = useCallback(async () => {
        if (!companyId || !documentId || !bodyId) {
            return {events: [], total: 0};
        }

        setIsFetching(true);
        try {
            const querySequence = [
                {type: EVENT_TYPE_CONFIRMED_QTY, apply: (payload) => applyEventsPayload(payload)},
                {type: EVENT_TYPE_REQUEST_CONFIRMED_QTY, apply: (payload) => applyRequestedEventsPayload(payload)},
            ];
            const responses = await Promise.allSettled(
                querySequence.map((config) => fetchDocumentBodyEvents({
                    companyId,
                    documentId,
                    bodyId,
                    params: {type: config.type},
                }))
            );

            let confirmedResult = {events: [], total: 0};
            let errorMessage = null;

            responses.forEach((result, index) => {
                const config = querySequence[index];
                if (result.status === "fulfilled") {
                    const applied = config.apply(result.value);
                    if (config.type === EVENT_TYPE_CONFIRMED_QTY) {
                        confirmedResult = applied;
                    }
                    return;
                }
                if (!errorMessage) {
                    errorMessage = result.reason?.message
                        ?? t("app.common.generic_error", {defaultValue: "Qualcosa è andato storto"});
                }
                if (config.type === EVENT_TYPE_CONFIRMED_QTY) {
                    applyEventsPayload({events: [], total: 0});
                } else {
                    applyRequestedEventsPayload({events: [], total: 0});
                }
            });

            if (errorMessage) {
                toast.error(errorMessage);
            }

            return confirmedResult;
        } catch (error) {
            toast.error(error?.message ?? t("app.common.generic_error", {defaultValue: "Qualcosa è andato storto"}));
            return {events: [], total: 0};
        } finally {
            setIsFetching(false);
        }
    }, [applyEventsPayload, applyRequestedEventsPayload, bodyId, companyId, documentId, t]);

    useEffect(() => {
        if (!isOpen) {
            return;
        }

        let isMounted = true;
        setHasLoadedEvents(false);

        const loadEvents = async () => {
            const payload = await fetchEvents();
            if (isMounted) {
                notifyParent(payload);
            }
        };

        loadEvents();

        return () => {
            isMounted = false;
        };
    }, [fetchEvents, isOpen, notifyParent]);

    const handleClose = useCallback(() => {
        onClose();
        setEvents([]);
        setTotalEvents(0);
        setRowLoading({});
        setHasLoadedEvents(false);
        setActiveTab("confirmed");
        setRequestedEvents([]);
        setRequestedTotal(0);
        setNewEntry({
            quantity: "",
            eventDate: getCurrentServerDateTime(),
        });
    }, [onClose]);

    const handleNewEntryChange = useCallback((field, value) => {
        setNewEntry((prev) => {
            if (field === "quantity") {
                if (value === null || value === undefined || value === "") {
                    return {
                        ...prev,
                        quantity: "",
                    };
                }
                if (isAtCapacity) {
                    return {
                        ...prev,
                        quantity: "",
                    };
                }
                let nextQuantity = Number(value);
                if (!Number.isFinite(nextQuantity)) {
                    return prev;
                }
                if (nextQuantity < 0) {
                    nextQuantity = 0;
                }
                if (effectiveMaxQuantity !== null && nextQuantity > effectiveMaxQuantity) {
                    nextQuantity = effectiveMaxQuantity;
                }
                if (remainingQuantity !== null && nextQuantity > remainingQuantity) {
                    nextQuantity = remainingQuantity;
                }
                return {
                    ...prev,
                    quantity: nextQuantity,
                };
            }
            return {
                ...prev,
                [field]: field === "eventDate"
                    ? normalizeEventDate(value)
                    : (value ?? ""),
            };
        });
    }, [effectiveMaxQuantity, isAtCapacity, remainingQuantity]);

    const handleAddEvent = useCallback(async () => {
        if (!companyId || !documentId || !bodyId) {
            return;
        }

        if (isAtCapacity) {
            toast.error(t("app.documents.confirmed_quantity_no_available", {defaultValue: "Non è disponibile altra quantità da confermare"}));
            return;
        }

        const quantityValue = Number(newEntry.quantity);
        if (!Number.isFinite(quantityValue) || quantityValue <= 0) {
            toast.error(t("app.documents.confirmed_quantity_invalid", {defaultValue: "Inserisci una quantità valida"}));
            return;
        }

        if (effectiveMaxQuantity !== null && quantityValue > effectiveMaxQuantity) {
            toast.error(t("app.documents.confirmed_quantity_exceeds_total", {defaultValue: "La quantità non può superare la quantità totale disponibile"}));
            return;
        }

        if (effectiveMaxQuantity !== null) {
            const projectedTotal = effectiveEventsQuantity + quantityValue;
            if (projectedTotal - effectiveMaxQuantity > Number.EPSILON) {
                toast.error(t("app.documents.confirmed_quantity_sum_exceeds_total", {defaultValue: "La somma delle quantità confermate non può superare la quantità totale disponibile"}));
                return;
            }
        }

        const eventDateValue = normalizeEventDate(newEntry.eventDate);
        if (!eventDateValue) {
            toast.error(t("app.documents.confirmed_quantity_invalid_date", {defaultValue: "Inserisci una data valida"}));
            return;
        }

        setIsSubmitting(true);
        try {
            const response = await createDocumentBodyEvent({
                companyId,
                documentId,
                bodyId,
                data: {
                    type: EVENT_TYPE_CONFIRMED_QTY,
                    quantity: quantityValue,
                    event_date: eventDateValue,
                },
            });
            toast.success(t("app.documents.confirmed_quantity_added", {defaultValue: "Quantità confermata aggiunta"}));
            const result = handleApiListResponse(response);
            if (!result || result.events.length === 0) {
                const fallback = await fetchEvents();
                notifyParent(fallback);
            }
            setNewEntry({
                quantity: "",
                eventDate: getCurrentServerDateTime(),
            });
        } catch (error) {
            toast.error(error?.message ?? t("app.common.generic_error", {defaultValue: "Qualcosa è andato storto"}));
        } finally {
            setIsSubmitting(false);
        }
    }, [bodyId, companyId, documentId, effectiveEventsQuantity, effectiveMaxQuantity, fetchEvents, handleApiListResponse, isAtCapacity, newEntry.eventDate, newEntry.quantity, notifyParent, t]);

    const handleAddEventSubmit = useCallback((event) => {
        event.preventDefault();
        handleAddEvent();
    }, [handleAddEvent]);

    const handleRemoveEvent = useCallback(async (eventId) => {
        if (!eventId) {
            return;
        }

        setRowLoading((prev) => ({...prev, [eventId]: true}));
        try {
            const response = await deleteDocumentBodyEvent({companyId, documentId, bodyId, eventId});
            toast.success(t("app.documents.confirmed_quantity_removed", {defaultValue: "Quantità confermata rimossa"}));
            const result = handleApiListResponse(response);
            if (!result || result.events.length === 0) {
                const fallback = await fetchEvents();
                notifyParent(fallback);
            }
        } catch (error) {
            toast.error(error?.message ?? t("app.common.generic_error", {defaultValue: "Qualcosa è andato storto"}));
        } finally {
            setRowLoading((prev) => ({...prev, [eventId]: false}));
        }
    }, [bodyId, companyId, documentId, fetchEvents, handleApiListResponse, notifyParent, t]);

    const adjustRowQuantityToMatchConfirmed = useCallback(async (nextQuantityValue) => {
        if (!companyId || !documentId || !bodyId) {
            return false;
        }
        const normalizedQuantity = toNumericOrNull(nextQuantityValue);
        if (normalizedQuantity === null) {
            return false;
        }

        try {
            await updateDocumentBodyRow({
                companyId,
                documentId,
                bodyId,
                data: {
                    id: bodyId,
                    quantity: normalizedQuantity,
                },
            });
            setManagedMaxQuantity(normalizedQuantity);
            return true;
        } catch (error) {
            toast.error(
                error?.message
                ?? t("app.documents.confirmed_quantity_total_update_failed", {defaultValue: "Impossibile aggiornare la quantità totale della riga"})
            );
            return false;
        }
    }, [bodyId, companyId, documentId, t]);

    const handleUpdateRequestStatus = useCallback(async (eventId, nextStatus) => {
        if (!eventId || !companyId || !documentId || !bodyId) {
            return false;
        }
        if (!nextStatus) {
            return false;
        }

        setRowLoading((prev) => ({...prev, [eventId]: true}));
        let wasSuccessful = false;
        try {
            await updateDocumentBodyEvent({
                companyId,
                documentId,
                bodyId,
                eventId,
                data: {
                    id: eventId,
                    status: nextStatus,
                },
            });
            const successMessage = nextStatus === "rejected"
                ? t("app.documents.confirmed_quantity_request_rejected", {defaultValue: "Richiesta rifiutata"})
                : t("app.documents.confirmed_quantity_request_approved", {defaultValue: "Richiesta approvata"});
            toast.success(successMessage);
            const refreshed = await fetchEvents();
            notifyParent(refreshed);
            wasSuccessful = true;
        } catch (error) {
            toast.error(error?.message ?? t("app.common.generic_error", {defaultValue: "Qualcosa è andato storto"}));
        } finally {
            setRowLoading((prev) => ({...prev, [eventId]: false}));
        }
        return wasSuccessful;
    }, [bodyId, companyId, documentId, fetchEvents, notifyParent, t]);

    const handleApproveRequest = useCallback(async (eventId) => {
        if (!eventId) {
            return;
        }
        const targetEvent = requestedEvents.find((item) => {
            const candidateId = item?.id ?? item?.event_id ?? item?.uuid ?? null;
            if (candidateId === null || candidateId === undefined) {
                return false;
            }
            return String(candidateId) === String(eventId);
        });
        const requestQuantityValue = resolveEventQuantityValue(targetEvent);
        const projectedQuantityTotal = requestQuantityValue > 0
            ? effectiveEventsQuantity + requestQuantityValue
            : effectiveEventsQuantity;
        const shouldAdjustTotal = (
            effectiveMaxQuantity !== null
            && requestQuantityValue > 0
            && projectedQuantityTotal - effectiveMaxQuantity > Number.EPSILON
        );

        const wasSuccessful = await handleUpdateRequestStatus(eventId, "confirmed");
        if (wasSuccessful && shouldAdjustTotal) {
            await adjustRowQuantityToMatchConfirmed(projectedQuantityTotal);
        }
    }, [adjustRowQuantityToMatchConfirmed, effectiveEventsQuantity, effectiveMaxQuantity, handleUpdateRequestStatus, requestedEvents]);

    const handleRejectRequest = useCallback((eventId) => {
        handleUpdateRequestStatus(eventId, "rejected");
    }, [handleUpdateRequestStatus]);

    const modalTitle = useMemo(() => (
        t("app.documents.confirmed_quantity_title", {defaultValue: "Quantità confermate"})
    ), [t]);

    const listTitle = useMemo(() => {
        const base = t("app.documents.confirmed_quantity_list", {defaultValue: "Quantità registrate"});
        return totalEvents > 0 ? `${base} (${totalEvents})` : base;
    }, [t, totalEvents]);

    const requestedListTitle = useMemo(() => {
        const base = t("app.documents.confirmed_quantity_requests_list", {defaultValue: "Richieste di conferma"});
        return requestedTotal > 0 ? `${base} (${requestedTotal})` : base;
    }, [requestedTotal, t]);

    const tabs = useMemo(() => ([
        {
            id: "confirmed",
            name: t("app.documents.confirmed_quantity_tab_confirmed", {defaultValue: "Confermate"}),
            count: totalEvents,
        },
        {
            id: "requested",
            name: t("app.documents.confirmed_quantity_tab_requested", {defaultValue: "Richieste"}),
            count: requestedTotal,
        },
    ]), [requestedTotal, t, totalEvents]);

    const requestedEmptyMessage = useMemo(() => (
        t("app.documents.confirmed_quantity_requests_empty", {defaultValue: "Non sono presenti richieste di conferma"})
    ), [t]);

    const approveButtonLabel = useMemo(() => (
        t("app.documents.confirmed_quantity_request_approve_action", {defaultValue: "Approva"})
    ), [t]);

    const rejectButtonLabel = useMemo(() => (
        t("app.documents.confirmed_quantity_request_reject_action", {defaultValue: "Rifiuta"})
    ), [t]);

    return (
        <BoardPopup
            isOpen={isOpen}
            setIsOpen={(status) => {
                if (!status) {
                    handleClose();
                }
            }}
            canClose
            zIndexClass={'z-[200]'}
            setClean={() => {}}
        >
            <div className="-mt-4 mb-3 bg-white">
                <div className="px-4 sm:px-6">
                    <div className="flex items-center justify-between mt-2 mb-6">
                        <div className="flex items-start space-x-5">
                            <div className="pt-1.5">
                                <h1 className="text-xl font-bold text-blue-1000">{modalTitle}</h1>
                                <p className="mt-1 text-sm text-gray-500">
                                    {t("app.documents.confirmed_quantity_help", {defaultValue: "Gestisci le quantità confermate per questa riga di documento."})}
                                </p>
                            </div>
                        </div>
                    </div>
                    <div className="space-y-8">
                        <div>
                            <nav className="-mb-px flex space-x-4" aria-label="Tabs">
                                {tabs.map((tab) => (
                                    <button
                                        key={tab.id}
                                        type="button"
                                        onClick={() => setActiveTab(tab.id)}
                                        className={classNames(
                                            tab.id === activeTab
                                                ? "border-blue-500 text-blue-600"
                                                : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300",
                                            "whitespace-nowrap border-b-2 px-3 py-2 text-sm font-medium"
                                        )}
                                    >
                                        <span>{tab.name}</span>
                                        <span className="ml-2 rounded-full bg-gray-100 px-2 py-0.5 text-xs font-semibold text-gray-600">
                                            {tab.count}
                                        </span>
                                    </button>
                                ))}
                            </nav>
                        </div>

                        {activeTab === "confirmed" ? (
                            <div className="space-y-8">
                                <form
                                    onSubmit={handleAddEventSubmit}
                                    className="border-none pb-5 mt-3"
                                >
                                    <div className="mt-3 grid sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-y-4 gap-x-4">
                                        <div className="col-span-2 md:col-span-1">
                                            <InputQuantityNumber
                                                id={`confirmed-qty-input-${bodyId}`}
                                                label={t("app.documents.quantity", {defaultValue: "Quantità"})}
                                                value={newEntry.quantity}
                                                onChange={(value) => handleNewEntryChange("quantity", value)}
                                                isFullW
                                                isDisabled={isFetching || isSubmitting || isAtCapacity}
                                            />
                                        </div>
                                        <div className="col-span-2 md:col-span-1">
                                            <InputDate
                                                name={`confirmed-qty-new-${bodyId}`}
                                                label={t("app.documents.confirmed_quantity_date", {defaultValue: "Data"})}
                                                isShowTime
                                                startDate={newEntry.eventDate}
                                                onChange={(value) => handleNewEntryChange("eventDate", value)}
                                                extraClass="block"
                                            />
                                        </div>
                                        {formattedMaxQuantity !== null && (
                                            <div className="col-span-4 md:col-span-2">
                                                <p className="text-xs text-gray-500">
                                                    {t("app.documents.confirmed_quantity_remaining_hint", {
                                                        defaultValue: "Disponibile: {{remaining}} su {{total}}",
                                                        remaining: formattedRemainingQuantity ?? "0",
                                                        total: formattedMaxQuantity,
                                                    })}
                                                </p>
                                            </div>
                                        )}
                                        {isAtCapacity && (
                                            <div className="col-span-4 md:col-span-2">
                                                <p className="text-xs font-medium text-red-600">
                                                    {t("app.documents.confirmed_quantity_no_available", {defaultValue: "Non è disponibile altra quantità da confermare"})}
                                                </p>
                                            </div>
                                        )}
                                        <div className="col-span-4 md:col-span-2">
                                            <div className="flex flex-col items-center">
                                                <ButtonSubmit
                                                    isLoading={isSubmitting}
                                                    label={t("app.common.add", {defaultValue: "Aggiungi"})}
                                                    isFullWith={true}
                                                    disabled={isFetching || isAtCapacity}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </form>
                                <div>
                                    <h4 className="text-sm font-semibold uppercase tracking-wide text-gray-700">
                                        {listTitle}
                                    </h4>
                                    <LoaderWrapper isLoading={isFetching}>
                                        {!hasConfirmedEvents ? (
                                            <p className="mt-3 text-sm text-gray-500">
                                                {t("app.documents.confirmed_quantity_empty", {defaultValue: "Non sono presenti quantità confermate"})}
                                            </p>
                                        ) : events.length === 0 ? (
                                            <p className="mt-3 text-sm text-gray-500">
                                                {t("app.documents.confirmed_quantity_empty_list", {defaultValue: "Nessun evento disponibile da mostrare"})}
                                            </p>
                                        ) : (
                                            <ul className="mt-4 h-64 divide-y divide-gray-200 overflow-y-auto rounded-md border border-gray-200 bg-white">
                                                {events.map((eventItem, index) => {
                                                    const eventId = eventItem?.id;
                                                    const resolvedKey = eventId ?? `event-${index}`;
                                                    const eventUser = eventItem?.user ?? eventItem?.creator ?? eventItem?.created_by ?? null;
                                                    const userFullname = eventUser
                                                        ? (
                                                            eventUser.fullname
                                                            ?? [eventUser.first_name, eventUser.last_name].filter(Boolean).join(" ")
                                                            ?? eventUser.name
                                                            ?? eventUser.username
                                                            ?? eventUser.email
                                                        )
                                                        : null;
                                                    const normalizedDate = normalizeEventDate(
                                                        eventItem?.event_date
                                                        ?? eventItem?.eventDate
                                                        ?? eventItem?.created_at
                                                        ?? eventItem?.createdAt
                                                    );
                                                    const eventDate = normalizedDate ? getFormattedDate(normalizedDate) : "-";
                                                    const quantityValue = eventItem?.quantity ?? eventItem?.confirmed_quantity ?? null;
                                                    const unitSymbol = eventItem?.unit_symbol
                                                        ?? eventItem?.unitSymbol
                                                        ?? eventItem?.unit?.symbol
                                                        ?? eventItem?.unit_value?.symbol
                                                        ?? "";
                                                    const isDeleting = eventId ? Boolean(rowLoading[eventId]) : false;
                                                    const fallbackUserName = userFullname || t("app.common.unknown_user", {defaultValue: "Utente sconosciuto"});
                                                    const userInitialData = eventUser ?? {fullname: fallbackUserName};

                                                    return (
                                                        <li key={resolvedKey} className="px-4 py-3 text-sm lg:flex lg:items-center lg:justify-between">
                                                            <div className="flex flex-1 items-center">
                                                                <div className="flex items-center">
                                                                    <div className="flex-shrink-0">
                                                                        <UserInitial user={userInitialData} />
                                                                    </div>
                                                                    <div className="ml-3">
                                                                        <p className="text-sm font-medium text-gray-900">
                                                                            {fallbackUserName}
                                                                        </p>
                                                                        <p className="text-xs font-medium text-gray-700">
                                                                            {eventDate}
                                                                        </p>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                            <div className="mt-3 flex flex-shrink-0 items-center lg:mt-0">
                                                                <div className="mr-3 text-md font-semibold capitalize text-gray-900">
                                                                    {quantityValue !== null && quantityValue !== undefined ? (
                                                                        <NumberFormat
                                                                            value={quantityValue}
                                                                            displayType="text"
                                                                            thousandSeparator="."
                                                                            decimalSeparator=","
                                                                            suffix={unitSymbol ? ` ${unitSymbol}` : ""}
                                                                        />
                                                                    ) : (
                                                                        "-"
                                                                    )}
                                                                </div>
                                                                {eventId && (
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => handleRemoveEvent(eventId)}
                                                                        disabled={isDeleting}
                                                                        className="flex items-center justify-center text-sm font-medium text-red-500 transition hover:text-red-300 disabled:cursor-not-allowed disabled:opacity-70"
                                                                    >
                                                                        {isDeleting ? (
                                                                            t("app.common.loading", {defaultValue: "Caricamento"})
                                                                        ) : (
                                                                            <TrashIcon className="h-4 w-4" aria-hidden="true" />
                                                                        )}
                                                                    </button>
                                                                )}
                                                            </div>
                                                        </li>
                                                    );
                                                })}
                                            </ul>
                                        )}
                                    </LoaderWrapper>
                                </div>
                            </div>
                        ) : (
                            <div>
                                <h4 className="text-sm font-semibold uppercase tracking-wide text-gray-700">
                                    {requestedListTitle}
                                </h4>
                                <LoaderWrapper isLoading={isFetching}>
                                    {!hasRequestedEvents ? (
                                        <p className="mt-3 text-sm text-gray-500">
                                            {requestedEmptyMessage}
                                        </p>
                                    ) : (
                                        <ul className="mt-4 h-64 divide-y divide-gray-200 overflow-y-auto rounded-md border border-gray-200 bg-white">
                                            {requestedEvents.map((eventItem, index) => {
                                                const requestId = eventItem?.id ?? eventItem?.event_id ?? eventItem?.uuid ?? null;
                                                const rowKey = requestId ?? `request-${index}`;
                                                const eventUser = eventItem?.user ?? eventItem?.creator ?? eventItem?.created_by ?? null;
                                                const userFullname = eventUser
                                                    ? (
                                                        eventUser.fullname
                                                        ?? [eventUser.first_name, eventUser.last_name].filter(Boolean).join(" ")
                                                        ?? eventUser.name
                                                        ?? eventUser.username
                                                        ?? eventUser.email
                                                    )
                                                    : null;
                                                const normalizedDate = normalizeEventDate(
                                                    eventItem?.event_date
                                                    ?? eventItem?.eventDate
                                                    ?? eventItem?.created_at
                                                    ?? eventItem?.createdAt
                                                );
                                                const eventDate = normalizedDate ? getFormattedDate(normalizedDate) : "-";
                                                const quantityValue = resolveEventQuantityValue(eventItem);
                                                const unitSymbol = eventItem?.unit_symbol
                                                    ?? eventItem?.unitSymbol
                                                    ?? eventItem?.unit?.symbol
                                                    ?? eventItem?.unit_value?.symbol
                                                    ?? "";
                                                const fallbackUserName = userFullname || t("app.common.unknown_user", {defaultValue: "Utente sconosciuto"});
                                                const userInitialData = eventUser ?? {fullname: fallbackUserName};
                                                const isProcessing = Boolean(requestId && rowLoading[requestId]);

                                                return (
                                                    <li key={rowKey} className="px-4 py-3 text-sm lg:flex lg:items-center lg:justify-between">
                                                        <div className="flex flex-1 items-center">
                                                            <div className="flex items-center">
                                                                <div className="flex-shrink-0">
                                                                    <UserInitial user={userInitialData} />
                                                                </div>
                                                                <div className="ml-3">
                                                                    <p className="text-sm font-medium text-gray-900">
                                                                        {fallbackUserName}
                                                                    </p>
                                                                    <p className="text-xs font-medium text-gray-700">
                                                                        {eventDate}
                                                                    </p>
                                                                </div>
                                                            </div>
                                                        </div>
                                                        <div className="mt-3 flex flex-shrink-0 items-center gap-3 lg:mt-0">
                                                            <div className="text-sm font-semibold text-gray-900">
                                                                <NumberFormat
                                                                    value={quantityValue}
                                                                    displayType="text"
                                                                    thousandSeparator="."
                                                                    decimalSeparator=","
                                                                    suffix={unitSymbol ? ` ${unitSymbol}` : ""}
                                                                />
                                                            </div>
                                                            {requestId && (
                                                                <div className="flex items-center gap-2">
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => handleRejectRequest(requestId)}
                                                                        disabled={isProcessing || isFetching}
                                                                        className="rounded-md border border-red-500 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-red-600 transition hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-60"
                                                                    >
                                                                        {isProcessing ? (
                                                                            t("app.common.loading", {defaultValue: "Caricamento"})
                                                                        ) : (
                                                                            rejectButtonLabel
                                                                        )}
                                                                    </button>
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => handleApproveRequest(requestId)}
                                                                        disabled={isProcessing || isFetching}
                                                                        className="rounded-md border border-green-600 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-green-700 transition hover:bg-green-50 disabled:cursor-not-allowed disabled:opacity-60"
                                                                    >
                                                                        {isProcessing ? (
                                                                            t("app.common.loading", {defaultValue: "Caricamento"})
                                                                        ) : (
                                                                            approveButtonLabel
                                                                        )}
                                                                    </button>
                                                                </div>
                                                            )}
                                                        </div>
                                                    </li>
                                                );
                                            })}
                                        </ul>
                                    )}
                                </LoaderWrapper>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </BoardPopup>
    );
};

DocumentBodyConfirmedQuantityModal.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    companyId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    documentId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    bodyId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    maxQuantity: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    initialQuantityTotal: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    onEventsChange: PropTypes.func,
};

DocumentBodyConfirmedQuantityModal.defaultProps = {
    companyId: null,
    documentId: null,
    bodyId: null,
    maxQuantity: null,
    initialQuantityTotal: null,
    onEventsChange: null,
};

export default DocumentBodyConfirmedQuantityModal;
